// pageFeedbackService — per-user thumbs up/down (+ optional comment) on a page.
// Backs the subheader feedback action. Every vote and comment is logged as a
// new row, so changing the vote never loses a comment; the user's current vote
// is their latest row (see es-engage-api `pageFeedback` controller).
//
// The API speaks "sentiment" (positive | negative | none); the UI speaks a vote
// ("up" | "down" | null). The mapping helpers below are the single translation
// point so callers never hand-roll the sentiment strings.
import { apiFetch, apiUrl } from "./apiClient";

export const SENTIMENT = {
  UP: "positive",
  DOWN: "negative",
  NONE: "none",
};

// UI vote -> API sentiment. `null`/unknown clears the vote ("none").
export const voteToSentiment = (vote) => {
  if (vote === "up") {
    return SENTIMENT.UP;
  }
  if (vote === "down") {
    return SENTIMENT.DOWN;
  }
  return SENTIMENT.NONE;
};

// API sentiment -> UI vote. Anything that isn't an explicit up/down reads as no
// vote, so the thumbs render neutral.
export const sentimentToVote = (sentiment) => {
  if (sentiment === SENTIMENT.UP) {
    return "up";
  }
  if (sentiment === SENTIMENT.DOWN) {
    return "down";
  }
  return null;
};

// Fetch this user's latest feedback for a page. Returns that record
// ({ sentiment, ... }) or null when they haven't voted. Resolves null
// (never throws) so a failed read just renders neutral thumbs.
export async function getPageFeedback(url) {
  try {
    const response = await apiFetch(
      apiUrl(`/pageFeedback?url=${encodeURIComponent(url)}`),
    );
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error("Page feedback fetch error:", error);
    return null;
  }
}

// Log this user's vote for a page. `sentiment` must be one of SENTIMENT. An
// optional `comment` is saved in the same row. Returns the saved record or
// null on failure.
export async function submitPageFeedback({ url, sentiment, comment }) {
  try {
    const response = await apiFetch(apiUrl("/pageFeedback"), {
      option: "POST",
      body: { url, sentiment, comment },
    });
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error("Page feedback submit error:", error);
    return null;
  }
}
